import { Mail, Phone, Place } from '@mui/icons-material';
import WhatsApp from '@mui/icons-material/WhatsApp';
import { type ButtonProps, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import ChipLabel, { type CHIP_COLORS } from '/@/components/chip-label';
import useBreadcrumbs from '/@/hooks/breadcrumbs';
import { useGetUserNode, useOwnUserNode } from '/@/hooks/nodes';
import { useHasWriteAccess } from '/@/hooks/session';
import { _ } from '/@/locale';
import { openWhatsappWeb } from '/@/utils';
import { useTabs } from '../..';
import AncestorsBar from '../../components/ancestors-bar';
import FieldviewChip from '../../components/fieldview-chip';
import GrowersListActions from '../../growers/components/growers-list-actions';
import NewFieldGrowersModal from '../../growers/components/new-field-modal';
import GrowerDashboard from '../components/dashboard';

const SUBTYPE_LABELS: Record<string, string> = {
    common: _('Otros segmentos'),
    premium: _('Premium'),
};

const GrowerDashboardDetail = ({ growerNodeId }: { growerNodeId: number }) => {
    const growerQuery = useGetUserNode(growerNodeId);
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);
    const [moreOptionsModalOpen, setMoreOptionsModalOpen] = useState(false);
    const { tab, handleTabChange } = useTabs();
    const breadcrumbs = useBreadcrumbs('grower.GrowerDetail');
    const ownUserNodeQuery = useOwnUserNode();

    const hasWriteAccess = useHasWriteAccess();

    const contactItems = useMemo(
        () =>
            [
                { key: 'location', Icon: Place, value: growerQuery.data?.location },
                { key: 'email', Icon: Mail, value: growerQuery.data?.email },
                { key: 'phone', Icon: Phone, value: growerQuery.data?.phone },
            ].filter((item): item is { key: string; Icon: typeof Place; value: string } => Boolean(item.value)),
        [growerQuery.data?.location, growerQuery.data?.email, growerQuery.data?.phone]
    );

    const headerButtons = useMemo((): ButtonProps[] => {
        if (!hasWriteAccess) return [];
        return [
            {
                children: _('Contactar'),
                variant: 'outlined',
                color: 'secondary',
                size: 'small',
                startIcon: <WhatsApp />,
                onClick: () => openWhatsappWeb({ phone: growerQuery.data?.phone }),
            },
            {
                children: _('Agregar lote'),
                variant: 'contained',
                color: 'secondary',
                size: 'medium',
                onClick: () => setNewFieldModalOpen(true),
            },
            {
                children: (
                    <GrowersListActions
                        growerNodeId={growerNodeId}
                        editable={true}
                        growerPhone={growerQuery.data?.phone || ''}
                        growerName={growerQuery.data?.name || ''}
                        tab='current-campaign'
                        onGrowerDeleteSuccess={() => {}}
                        deletable={true}
                        isInDetailPage
                    />
                ),
                variant: 'text',
                sx: {
                    p: 0,
                },
            },
        ];
    }, [hasWriteAccess, growerQuery.data?.phone]);

    const subtype = growerQuery.data?.subtype;
    const subtypeChipType = subtype ? `${subtype}Grower` : null;
    const subtypeChipLabel = subtype ? SUBTYPE_LABELS[subtype] : null;

    return (
        <PageContainer
            header={
                <Header
                    title={
                        growerQuery.data?.isSelf ? (
                            _('Mi cuenta')
                        ) : (
                            <span className='mp-mask'>{growerQuery.data?.name}</span>
                        )
                    }
                    titleIcon={
                        <Stack direction='row' spacing={2} alignItems='center' pl={1}>
                            {subtypeChipType && subtypeChipLabel && (
                                <ChipLabel
                                    type={subtypeChipType as keyof typeof CHIP_COLORS}
                                    label={subtypeChipLabel}
                                    sx={{ height: '32px' }}
                                />
                            )}
                            {growerQuery.data?.user && <FieldviewChip />}
                        </Stack>
                    }
                    breadcrumbs={breadcrumbs}
                    buttons={headerButtons}
                />
            }
        >
            <AncestorsBar
                contactItems={contactItems}
                ancestorsFromNodeId={growerNodeId !== ownUserNodeQuery.data?.id ? growerNodeId : undefined}
            />

            <Stack mt={4}>
                <GrowerDashboard growerNodeId={growerNodeId} tab={tab} handleTabChange={handleTabChange} />
            </Stack>
            <NewFieldGrowersModal
                open={newFieldModalOpen}
                setOpen={setNewFieldModalOpen}
                growerUserNodeId={growerNodeId}
                trackEntrySource='grower-dashboard-card'
            />
        </PageContainer>
    );
};

export default GrowerDashboardDetail;
